import { useMemo, useState } from "react";
import { trending, TrendingItem } from "@/mocks/gossip";

const cats = ["全部", "网络热议", "娱乐圈", "社会热点", "商业财经", "科技圈", "体育圈"];

function TrendRow({ item, active }: { item: TrendingItem; active: boolean }) {
  const [fold, setFold] = useState(true);
  const badgeCls =
    item.rank === 1
      ? "bg-primary-500 text-background-50"
      : item.rank <= 3
      ? "bg-accent-500 text-background-50"
      : "bg-background-200 text-foreground-700";
  return (
    <li className="group rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-200">
      <div className="flex items-center gap-4 p-3.5">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-heading text-sm font-black ${badgeCls}`}>
          {String(item.rank).padStart(2, "0")}
        </span>
        <button
          type="button"
          onClick={() => setFold((v) => !v)}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left"
          aria-expanded={!fold}
        >
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="truncate text-sm font-semibold text-foreground-900 group-hover:text-primary-700">{item.title}</span>
              {item.up ? (
                <i className="ri-arrow-up-line h-4 w-4 shrink-0 text-accent-600"></i>
              ) : (
                <i className="ri-arrow-down-line h-4 w-4 shrink-0 text-foreground-400"></i>
              )}
            </span>
            <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-foreground-600">
              <span className="rounded bg-background-100 px-1.5 py-0.5 text-foreground-700">{item.category}</span>
              <span className="whitespace-nowrap text-primary-600">{item.heat}</span>
            </span>
          </span>
          <i className={`ri-arrow-down-s-line h-5 w-5 shrink-0 text-foreground-400 transition-transform ${fold ? "" : "rotate-180"}`}></i>
        </button>
      </div>
      {!fold && (
        <div className="mx-4 mb-3 rounded-lg bg-background-100/70 p-3 text-sm leading-6 text-foreground-700">
          {item.desc}
        </div>
      )}
    </li>
  );
}

export default function HotList() {
  const [cat, setCat] = useState("全部");
  const [page, setPage] = useState(1);
  const per = 5;

  const filtered = useMemo(
    () => (cat === "全部" ? trending : trending.filter((t) => t.category === cat)),
    [cat]
  );
  const maxPage = Math.max(1, Math.ceil(filtered.length / per));
  const safePage = Math.min(page, maxPage);
  const shown = filtered.slice((safePage - 1) * per, safePage * per);

  return (
    <section id="hot" className="scroll-mt-28 bg-background-50 py-12 md:py-16">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-primary-600">Hot Ranking</span>
            <h2 className="mt-2 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
              今日瓜田热榜
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-7 text-foreground-600">
              按讨论热度与线索完整度综合排序。点击条目展开看点简介，先看结论，再决定要不要点开深扒专题。
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-foreground-600">
            <i className="ri-time-line h-4 w-4 text-secondary-600"></i>
            <span className="whitespace-nowrap">每 15 分钟刷新热度</span>
          </div>
        </div>

        {/* filter tabs */}
        <div className="nice-scroll mt-6 flex gap-2 overflow-x-auto pb-1">
          {cats.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                setCat(c);
                setPage(1);
              }}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                cat === c
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <ul className="space-y-3">
              {shown.map((t) => (
                <TrendRow key={t.rank} item={t} active />
              ))}
            </ul>

            {maxPage > 1 && (
              <div className="mt-6 flex items-center justify-center gap-3">
                <button
                  type="button"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700 transition-colors hover:bg-background-100 disabled:opacity-40"
                  aria-label="上一页"
                >
                  <i className="ri-arrow-left-s-line h-5 w-5"></i>
                </button>
                {Array.from({ length: maxPage }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n)}
                    className={`h-9 min-w-9 rounded-md px-2 text-sm font-semibold ${
                      n === safePage ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={safePage >= maxPage}
                  onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700 transition-colors hover:bg-background-100 disabled:opacity-40"
                  aria-label="下一页"
                >
                  <i className="ri-arrow-right-s-line h-5 w-5"></i>
                </button>
              </div>
            )}
          </div>

          {/* side explainer card */}
          <aside className="lg:col-span-4">
            <div className="rounded-2xl border border-secondary-200 bg-secondary-100/60 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary-500 text-background-50">
                <i className="ri-user-heart-line h-5 w-5"></i>
              </span>
              <h4 className="mt-3 font-heading text-lg font-bold text-foreground-950">怎么理解这份热榜？</h4>
              <p className="mt-2 text-sm leading-7 text-foreground-700">
                热度不等于可信度，更不等于真相。上榜只代表「大家都在讨论」，不代表「内容已被证实」。真正的深扒，请往下进入专题区。
              </p>
              <a href="#method" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-secondary-800 hover:text-secondary-900">
                学习理性吃瓜方法论
                <i className="ri-arrow-right-line h-4 w-4"></i>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}